import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import Login from './pages/Login'
import Register from './pages/Register'
import Onboarding from './pages/Onboarding'
import Dashboard from './pages/Dashboard'
import RenterDashboard from './pages/renter/RenterDashboard'
import OwnerDashboard from './pages/owner/OwnerDashboard'
import AgentDashboard from './pages/agent/AgentDashboard'
import PropertiesPage from './pages/Properties'
import LeaseDetailsPage from './pages/LeaseDetails'
import PaymentHistoryPage from './pages/PaymentHistory'
import PaymentMethods from './pages/renter/PaymentMethods'
import AutoPaySettings from './pages/renter/AutoPaySettings'
import PaymentSchedule from './pages/PaymentSchedule'
import PayRent from './pages/PayRent'
import SubmitMaintenanceRequest from './pages/SubmitMaintenanceRequest'
import SubmitReplacementRequest from './pages/SubmitReplacementRequest'
import FileComplaint from './pages/FileComplaint'
import NotificationCenter from './pages/NotificationCenter'
import OnboardProperty from './pages/owner/OnboardProperty'
import OwnerProperties from './pages/owner/OwnerProperties'
import OwnerPropertyDetail from './pages/owner/OwnerPropertyDetail'
import OwnerLeases from './pages/owner/OwnerLeases'
import OwnerLeaseDetail from './pages/owner/OwnerLeaseDetail'
import OwnerRentLedger from './pages/owner/OwnerRentLedger'
import TaxReports from './pages/owner/TaxReports'
import ComplianceManagement from './pages/owner/ComplianceManagement'
import ComplianceDetail from './pages/owner/ComplianceDetail'
import CreateComplianceCheck from './pages/owner/CreateComplianceCheck'
import RenterMaintenanceTracking from './pages/RenterMaintenanceTracking'
import RenterMaintenanceDetail from './pages/RenterMaintenanceDetail'
import RenterProfile from './pages/renter/RenterProfile'
import RenterSettings from './pages/renter/RenterSettings'
import ContractorProfile from './pages/contractor/ContractorProfile'
import ContractorSettings from './pages/contractor/ContractorSettings'
import AgentProfile from './pages/agent/AgentProfile'
import AgentSettings from './pages/agent/AgentSettings'
import AgentProperties from './pages/agent/AgentProperties'
import AgentPropertyDetail from './pages/agent/AgentPropertyDetail'
import AgentPropertyForm from './pages/agent/AgentPropertyForm'
import AgentLeases from './pages/agent/AgentLeases'
import AgentCreateLease from './pages/agent/AgentCreateLease'
import AgentLeaseDetail from './pages/agent/AgentLeaseDetail'
import AgentEditLease from './pages/agent/AgentEditLease'
import OwnerProfile from './pages/owner/OwnerProfile'
import OwnerSettings from './pages/owner/OwnerSettings'
import AdminProfile from './pages/admin/AdminProfile'
import AdminSettings from './pages/admin/AdminSettings'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminAuditLog from './pages/admin/AdminAuditLog'
import AgentMaintenanceDashboard from './pages/agent/AgentMaintenanceDashboard'
import AgentMaintenanceDetail from './pages/agent/AgentMaintenanceDetail'
import InspectionManagement from './pages/agent/InspectionManagement'
import CreateInspection from './pages/agent/CreateInspection'
import InspectionDetail from './pages/agent/InspectionDetail'
import UploadInspectionReport from './pages/agent/UploadInspectionReport'
import Messages from './pages/Messages'
import MessageThread from './pages/MessageThread'
import DocumentVault from './pages/DocumentVault'
import UploadDocument from './pages/UploadDocument'
import ContractorDashboard from './pages/contractor/ContractorDashboard'
import ContractorJobs from './pages/contractor/ContractorJobs'
import ContractorJobView from './pages/contractor/ContractorJobView'
import OverdueBanner from './components/OverdueBanner'
import Layout from './components/Layout'
import { AuthProvider } from './contexts/AuthContext'
import LandingPage from './pages/LandingPage'
import AgencyRegistration from './pages/agency/AgencyRegistration'
import AgencyDashboard from './pages/agency/AgencyDashboard'
import InviteAccept from './pages/InviteAccept'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute, { PublicOnlyRoute } from './components/ProtectedRoute'
import { ToastProvider } from './contexts/ToastContext'
import AdminAgenciesPage from './pages/admin/AdminAgenciesPage'
import AdminAgencyDetailPage from './pages/admin/AdminAgencyDetailPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminPropertiesPage from './pages/admin/AdminPropertiesPage'
import AdminEnterpriseCodesPage from './pages/admin/AdminEnterpriseCodesPage'
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage'

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
          <OverdueBanner />
          <Routes>
            <Route path="/" element={<Layout />}>
              {/* Public routes */}
              <Route index element={<LandingPage />} />
              <Route path="login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
              <Route path="register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
              <Route path="agency/register" element={<AgencyRegistration />} />
              <Route path="invite/accept" element={<InviteAccept />} />
              <Route path="invite/:token" element={<InviteAccept />} />

              {/* Protected routes - require authentication */}
              <Route path="onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

              {/* Renter routes */}
              <Route path="renter/dashboard" element={<ProtectedRoute allowedRoles={['RENTER']}><RenterDashboard /></ProtectedRoute>} />
              <Route path="renter/payment-methods" element={<ProtectedRoute allowedRoles={['RENTER']}><PaymentMethods /></ProtectedRoute>} />
              <Route path="renter/auto-pay" element={<ProtectedRoute allowedRoles={['RENTER']}><AutoPaySettings /></ProtectedRoute>} />
              <Route path="renter/pay-rent" element={<ProtectedRoute allowedRoles={['RENTER']}><PayRent /></ProtectedRoute>} />
              <Route path="renter/profile" element={<ProtectedRoute allowedRoles={['RENTER']}><RenterProfile /></ProtectedRoute>} />
              <Route path="renter/settings" element={<ProtectedRoute allowedRoles={['RENTER']}><RenterSettings /></ProtectedRoute>} />
              <Route path="payment-history" element={<ProtectedRoute allowedRoles={['RENTER']}><PaymentHistoryPage /></ProtectedRoute>} />
              <Route path="payment-schedule" element={<ProtectedRoute allowedRoles={['RENTER']}><PaymentSchedule /></ProtectedRoute>} />
              <Route path="maintenance/new" element={<ProtectedRoute allowedRoles={['RENTER']}><SubmitMaintenanceRequest /></ProtectedRoute>} />
              <Route path="maintenance/track" element={<ProtectedRoute allowedRoles={['RENTER']}><RenterMaintenanceTracking /></ProtectedRoute>} />
              <Route path="maintenance/:id" element={<ProtectedRoute allowedRoles={['RENTER']}><RenterMaintenanceDetail /></ProtectedRoute>} />
              <Route path="replacements/new" element={<ProtectedRoute allowedRoles={['RENTER']}><SubmitReplacementRequest /></ProtectedRoute>} />
              <Route path="complaints/new" element={<ProtectedRoute allowedRoles={['RENTER']}><FileComplaint /></ProtectedRoute>} />

              {/* Owner routes */}
              <Route path="owner/dashboard" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerDashboard /></ProtectedRoute>} />
              <Route path="owner/properties" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerProperties /></ProtectedRoute>} />
              <Route path="owner/properties/onboard" element={<ProtectedRoute allowedRoles={['OWNER']}><OnboardProperty /></ProtectedRoute>} />
              <Route path="owner/properties/:id" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerPropertyDetail /></ProtectedRoute>} />
              <Route path="owner/ledger" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerRentLedger /></ProtectedRoute>} />
              <Route path="owner/leases" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerLeases /></ProtectedRoute>} />
              <Route path="owner/leases/:id" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerLeaseDetail /></ProtectedRoute>} />
              <Route path="owner/tax-reports" element={<ProtectedRoute allowedRoles={['OWNER']}><TaxReports /></ProtectedRoute>} />
              <Route path="owner/compliance" element={<ProtectedRoute allowedRoles={['OWNER']}><ComplianceManagement /></ProtectedRoute>} />
              <Route path="owner/compliance/create" element={<ProtectedRoute allowedRoles={['OWNER']}><CreateComplianceCheck /></ProtectedRoute>} />
              <Route path="owner/compliance/:id" element={<ProtectedRoute allowedRoles={['OWNER']}><ComplianceDetail /></ProtectedRoute>} />
              <Route path="owner/profile" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerProfile /></ProtectedRoute>} />
              <Route path="owner/settings" element={<ProtectedRoute allowedRoles={['OWNER']}><OwnerSettings /></ProtectedRoute>} />

              {/* Agent routes */}
              <Route path="agent/dashboard" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentDashboard /></ProtectedRoute>} />
              <Route path="agent/properties" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentProperties /></ProtectedRoute>} />
              <Route path="agent/properties/new" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentPropertyForm /></ProtectedRoute>} />
              <Route path="agent/properties/:id" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentPropertyDetail /></ProtectedRoute>} />
              <Route path="agent/properties/:id/edit" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentPropertyForm /></ProtectedRoute>} />
              <Route path="agent/leases" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentLeases /></ProtectedRoute>} />
              <Route path="agent/leases/new" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentCreateLease /></ProtectedRoute>} />
              <Route path="agent/leases/:id" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentLeaseDetail /></ProtectedRoute>} />
              <Route path="agent/leases/:id/edit" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentEditLease /></ProtectedRoute>} />
              <Route path="agent/maintenance" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentMaintenanceDashboard /></ProtectedRoute>} />
              <Route path="agent/maintenance/:id" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentMaintenanceDetail /></ProtectedRoute>} />
              <Route path="agent/inspections" element={<ProtectedRoute allowedRoles={['AGENT']}><InspectionManagement /></ProtectedRoute>} />
              <Route path="agent/inspections/create" element={<ProtectedRoute allowedRoles={['AGENT']}><CreateInspection /></ProtectedRoute>} />
              <Route path="agent/inspections/:id" element={<ProtectedRoute allowedRoles={['AGENT']}><InspectionDetail /></ProtectedRoute>} />
              <Route path="agent/inspections/:id/report" element={<ProtectedRoute allowedRoles={['AGENT']}><UploadInspectionReport /></ProtectedRoute>} />
              <Route path="agent/profile" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentProfile /></ProtectedRoute>} />
              <Route path="agent/settings" element={<ProtectedRoute allowedRoles={['AGENT']}><AgentSettings /></ProtectedRoute>} />

              {/* Contractor routes */}
              <Route path="contractor/dashboard" element={<ProtectedRoute allowedRoles={['CONTRACTOR']}><ContractorDashboard /></ProtectedRoute>} />
              <Route path="contractor/jobs" element={<ProtectedRoute allowedRoles={['CONTRACTOR']}><ContractorJobs /></ProtectedRoute>} />
              <Route path="contractor/profile" element={<ProtectedRoute allowedRoles={['CONTRACTOR']}><ContractorProfile /></ProtectedRoute>} />
              <Route path="contractor/settings" element={<ProtectedRoute allowedRoles={['CONTRACTOR']}><ContractorSettings /></ProtectedRoute>} />
              <Route path="contractor/job/:token" element={<ProtectedRoute allowedRoles={['CONTRACTOR']}><ContractorJobView /></ProtectedRoute>} />

              {/* Admin routes */}
              <Route path="admin/dashboard" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="admin/profile" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminProfile /></ProtectedRoute>} />
              <Route path="admin/settings" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminSettings /></ProtectedRoute>} />
              <Route path="admin/audit" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminAuditLog /></ProtectedRoute>} />
              <Route path="admin/agencies" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminAgenciesPage /></ProtectedRoute>} />
              <Route path="admin/agencies/:id" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminAgencyDetailPage /></ProtectedRoute>} />
              <Route path="admin/users" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminUsersPage /></ProtectedRoute>} />
              <Route path="admin/properties" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminPropertiesPage /></ProtectedRoute>} />
              <Route path="admin/enterprise-codes" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminEnterpriseCodesPage /></ProtectedRoute>} />
              <Route path="admin/enterprise-codes/create" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminEnterpriseCodesPage /></ProtectedRoute>} />
              <Route path="admin/subscriptions" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminSubscriptionsPage /></ProtectedRoute>} />
              <Route path="agency/dashboard" element={<ProtectedRoute allowedRoles={['ADMIN']}><AgencyDashboard /></ProtectedRoute>} />

              {/* Shared protected routes - any authenticated user */}
              <Route path="properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
              <Route path="leases/:id" element={<ProtectedRoute><LeaseDetailsPage /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
              <Route path="messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="messages/:type/:id" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
              <Route path="documents" element={<ProtectedRoute><DocumentVault /></ProtectedRoute>} />
              <Route path="documents/upload" element={<ProtectedRoute><UploadDocument /></ProtectedRoute>} />

              {/* Catch all - redirect to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
)
